import type { ReactElement } from 'react';
import {
  useInkColorsQuery,
  useInvalidateInkColors,
  useProductInkColorsQuery,
} from '@/entities/ink-color';
import { allowedIdsKeyOf } from '../model/section-keys';
import { useSetProductInkColors } from '../model/use-set-product-ink-colors';
import { SectionCard } from './section-card';
import { TextOptionAllowForm } from './text-option-allow-form';

/** 허용 해제 영향 — 정책 docs/products/custom-policy.md §3-4. */
const REMOVAL_IMPACT =
  '기존 디자인의 글자 색은 그대로지만, 해제한 잉크를 쓴 디자인은 다른 잉크로 바꿔야 다시 저장할 수 있습니다.';

interface InkColorAllowSectionProps {
  readonly productId: string;
  readonly onSaved: (message: string) => void;
}

/** 상품의 허용 잉크 색상 — FontAllowSection 과 대칭. 칩마다 견본을 함께 그린다. */
export const InkColorAllowSection = ({
  productId,
  onSaved,
}: InkColorAllowSectionProps): ReactElement => {
  const invalidate = useInvalidateInkColors();
  const inkColorsQuery = useInkColorsQuery();
  const allowedQuery = useProductInkColorsQuery(productId);
  const saveAction = useSetProductInkColors({
    onSuccess: () => {
      onSaved('허용 잉크 색상을 저장했습니다.');
      invalidate.forProduct(productId);
    },
    // 다른 화면에서 지운 잉크가 섞이면 400 이다 — 잉크 목록과 허용 목록을 다시 불러 지운 잉크를 걷어 낸다.
    onError: () => {
      invalidate.list();
      invalidate.forProduct(productId);
    },
  });

  const inkColors = inkColorsQuery.data;
  const allowed = allowedQuery.data;
  const loadError = inkColorsQuery.error ?? allowedQuery.error;

  return (
    <SectionCard
      title="허용 잉크 색상"
      description="고객 에디터에서 이 상품에 쓸 수 있는 글자 색입니다. 잉크 색상 목록은 기준 정보 관리에서 관리합니다."
      error={saveAction.error?.message ?? null}
    >
      {inkColors !== undefined && allowed !== undefined ? (
        <TextOptionAllowForm
          key={allowedIdsKeyOf(allowed.map((inkColor) => inkColor.id))}
          options={inkColors.map((inkColor) => ({
            id: inkColor.id,
            label: inkColor.name,
            swatch: inkColor.colorCode,
          }))}
          initialIds={allowed.map((inkColor) => inkColor.id)}
          itemLabel="잉크 색상"
          removalImpact={REMOVAL_IMPACT}
          isSaving={saveAction.isPending}
          onSave={(inkColorIds) => saveAction.setProductInkColors({ productId, inkColorIds })}
        />
      ) : loadError !== null ? (
        <p role="alert" className="text-destructive">
          허용 잉크 색상을 불러오지 못했습니다: {loadError.message}
        </p>
      ) : (
        <p className="text-muted-foreground">불러오는 중…</p>
      )}
    </SectionCard>
  );
};
